const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:3001/api";

function getToken(): string | null {
  return localStorage.getItem("iif_token");
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
      ...(options.headers || {}),
    },
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Request failed: ${res.status}`);
  }

  return res.json();
}

// ==================== 产品 ====================
export interface Product {
  id: string;
  name: string;
  slug: string;
  category: "clothes" | "shoes" | "bags" | "nails";
  price: number;
  originalPrice?: number;
  images: string[];
  shortDescription: string;
  description: string;
  material: string;
  fit: string;
  occasions: string[];
  sizes: string[];
  colors: { name: string; hex: string }[];
  isNew?: boolean;
  isFeatured?: boolean;
  isActive?: boolean;
  rating: number;
  reviewCount: number;
  fabricLabel: string;
}

export function fetchProducts(params?: {
  category?: string;
  featured?: boolean;
  isNew?: boolean;
}): Promise<Product[]> {
  const qs = new URLSearchParams();
  if (params?.category) qs.set("category", params.category);
  if (params?.featured) qs.set("featured", "true");
  if (params?.isNew) qs.set("isNew", "true");
  const query = qs.toString();
  return request(`/products${query ? `?${query}` : ""}`);
}

export function fetchProductBySlug(slug: string): Promise<Product> {
  return request(`/products/${slug}`);
}

// ==================== 编辑文章 ====================
export interface GlowBlock {
  type: "paragraph" | "heading" | "subheading" | "quote" | "image" | "divider";
  content?: string;
  src?: string;
  alt?: string;
  caption?: string;
}

export interface GlowPost {
  id: string;
  slug: string;
  tag: string;
  date: string;
  readTime: string;
  title: string;
  subtitle: string;
  excerpt: string;
  coverImage: string;
  body: GlowBlock[];
  isActive?: boolean;
}

export function fetchEditorialPosts(): Promise<GlowPost[]> {
  return request("/editorial");
}

export function fetchEditorialBySlug(slug: string): Promise<GlowPost> {
  return request(`/editorial/${slug}`);
}

// ==================== 评论 ====================
export interface Review {
  id: string;
  author: string;
  avatar: string;
  location: string;
  rating: number;
  title: string;
  body: string;
  date: string;
  verified: boolean;
  helpful: number;
  size: string;
  fit: "Runs Small" | "True to Size" | "Runs Large";
  images?: string[];
}

export function fetchReviews(productId: string): Promise<Review[]> {
  return request(`/reviews/${productId}`);
}

// ==================== 认证 ====================
export interface AuthResponse {
  token: string;
  user: { id: string; email: string; name: string; role: string };
}

export function login(email: string, password: string): Promise<AuthResponse> {
  return request("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}

export function register(email: string, password: string, name?: string): Promise<AuthResponse> {
  return request("/auth/register", {
    method: "POST",
    body: JSON.stringify({ email, password, name }),
  });
}

export function fetchCurrentUser(): Promise<{ user: AuthResponse["user"] }> {
  return request("/auth/me");
}

// ==================== 订单 ====================
export interface OrderData {
  email: string;
  phone?: string;
  firstName: string;
  lastName: string;
  company?: string;
  address: string;
  apt?: string;
  city: string;
  state: string;
  zip: string;
  country?: string;
  subtotal: number;
  shippingCost: number;
  promoDiscount: number;
  tax: number;
  total: number;
  shippingMethod: string;
  items: {
    productId: string;
    name: string;
    price: number;
    size: string;
    color: string;
    colorHex: string;
    quantity: number;
    image: string;
  }[];
}

export function createOrder(data: OrderData): Promise<{ orderNumber: string; orderId: string }> {
  return request("/orders", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function fetchMyOrders(): Promise<unknown[]> {
  return request("/orders/my");
}

export interface OrderDetail {
  id: string;
  orderNumber: string;
  status: string;
  email: string;
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  subtotal: number;
  shippingCost: number;
  promoDiscount: number;
  tax: number;
  total: number;
  shippingMethod: string;
  items: {
    id: string;
    name: string;
    price: number;
    size: string;
    color: string;
    quantity: number;
    image: string;
  }[];
  createdAt: string;
}

export function trackOrder(orderNumber: string, email: string): Promise<OrderDetail> {
  return request("/orders/track", {
    method: "POST",
    body: JSON.stringify({ orderNumber, email }),
  });
}

// ==================== 管理后台 ====================
export function adminFetchProducts(): Promise<Product[]> {
  return request("/admin/products");
}

export function adminCreateProduct(product: Partial<Product>): Promise<Product> {
  return request("/admin/products", {
    method: "POST",
    body: JSON.stringify(product),
  });
}

export function adminUpdateProduct(id: string, data: Partial<Product>): Promise<Product> {
  return request(`/admin/products/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function adminDeleteProduct(id: string): Promise<void> {
  return request(`/admin/products/${id}`, { method: "DELETE" });
}

export function adminFetchEditorial(): Promise<GlowPost[]> {
  return request("/admin/editorial");
}

export function adminUpdateEditorial(id: string, data: Partial<GlowPost>): Promise<GlowPost> {
  return request(`/admin/editorial/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function adminFetchOrders(status?: string): Promise<unknown[]> {
  const qs = status ? `?status=${status}` : "";
  return request(`/admin/orders${qs}`);
}

export function adminUpdateOrder(id: string, status: string): Promise<unknown> {
  return request(`/admin/orders/${id}`, {
    method: "PUT",
    body: JSON.stringify({ status }),
  });
}

export function adminFetchStats(): Promise<{
  products: number;
  orders: number;
  users: number;
  revenue: number;
}> {
  return request("/admin/stats");
}

// ==================== 图片上传 ====================
export async function uploadImages(files: File[]): Promise<string[]> {
  const formData = new FormData();
  files.forEach((f) => formData.append("images", f));

  const token = getToken();
  const res = await fetch(`${API_BASE}/upload`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || "上传失败");
  }

  const data = await res.json();
  // 返回完整 URL
  const base = API_BASE.replace("/api", "");
  return (data.urls as string[]).map((u) => `${base}${u}`);
}

// ==================== Token 管理 ====================
export function setToken(token: string) {
  localStorage.setItem("iif_token", token);
}

export function clearToken() {
  localStorage.removeItem("iif_token");
}

export function hasToken(): boolean {
  return !!localStorage.getItem("iif_token");
}
